// Photo capture surface. Uses the native camera-or-file picker pattern
// (input type="file" capture="environment"), resizes client-side to
// 1600px on the long edge, runs a quality check (blur + glare + dark
// detection), then POSTs multipart to /api/medications/ocr.
//
// Two-photo flow: senior takes the front, then is prompted to take
// a second photo of the side panel (where the dosing instructions
// and NDC live on most bottles). Both ship to the backend in one
// multipart request and Cloud Vision OCRs each separately before the
// raw text is merged for RxNorm normalization. This recovers labels
// that wrap around cylindrical bottles where one photo can never
// capture everything.
//
// Quality gate: each photo is scored locally before upload. Blurry,
// glary, or too-dark shots prompt a retake instead of burning Vision
// quota on a label we already know we can't read.
//
// Playbook reference: Section 5.9.

import { BlockStack, SkeletonBodyText } from '@shopify/polaris';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type OcrCandidate, type OcrDosingHint, useOcrUpload } from '../api/ocr';
import { AppPage } from '../layouts/AppPage';
import { assessImageQuality } from '../lib/imageQuality';
import { CandidatePicker } from './CandidatePicker';
import { SeniorBanner } from './senior/SeniorBanner';
import { SeniorButton } from './senior/SeniorButton';
import { SeniorCard } from './senior/SeniorCard';

const MAX_DIMENSION = 1600;

type State =
  | { kind: 'idle' }
  | { kind: 'quality-warn'; message: string; pendingBlob: Blob; previewUrl: string }
  | { kind: 'front-ready'; frontBlob: Blob; frontPreviewUrl: string }
  | { kind: 'uploading'; previewUrls: string[] }
  | {
      kind: 'candidates';
      candidates: OcrCandidate[];
      dosingHint: OcrDosingHint | null;
      confidence: 'high' | 'medium' | 'low';
    }
  | { kind: 'no-candidates'; rawText: string }
  | { kind: 'error'; message: string };

export function PhotoCapture() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<State>({ kind: 'idle' });
  const navigate = useNavigate();
  const ocrUpload = useOcrUpload();

  async function captureNext(file: File, skipQualityWarn = false) {
    const resized = await resizeImage(file, MAX_DIMENSION);
    const previewUrl = URL.createObjectURL(resized);
    if (!skipQualityWarn) {
      try {
        const quality = await assessImageQuality(resized);
        if (!quality.ok && quality.message) {
          setState({
            kind: 'quality-warn',
            message: quality.message,
            pendingBlob: resized,
            previewUrl,
          });
          return;
        }
      } catch {
        // Quality assessment is opportunistic; fall through on errors.
      }
    }
    // First photo captured → offer to add a second (side panel).
    if (state.kind !== 'front-ready') {
      setState({ kind: 'front-ready', frontBlob: resized, frontPreviewUrl: previewUrl });
      return;
    }
    // Second photo captured → upload both.
    await submitPhotos([state.frontBlob, resized], [state.frontPreviewUrl, previewUrl]);
  }

  async function submitPhotos(blobs: Blob[], previewUrls: string[]) {
    setState({ kind: 'uploading', previewUrls });
    try {
      const result = await ocrUpload(blobs);
      let candidates = result.candidates;
      if (candidates.length === 0) {
        const fallbackName =
          result.geminiName || result.fdaLabel?.brandName || result.fdaLabel?.genericName || null;
        if (fallbackName) {
          const ingredientName = result.fdaLabel?.genericName?.toLowerCase().trim() || null;
          const ndcSegment = result.fdaLabel?.ndc
            ? `ndc-${result.fdaLabel.ndc}`
            : `name-${fallbackName.toLowerCase().replace(/\W+/g, '-')}`;
          candidates = [
            {
              rxcui: `synthetic-${ndcSegment}`,
              displayName: fallbackName,
              ingredientRxcui: null,
              ingredientName,
              score: 0.5,
            },
          ];
        }
      }
      if (candidates.length === 0) {
        setState({ kind: 'no-candidates', rawText: result.rawText ?? '' });
      } else {
        setState({
          kind: 'candidates',
          candidates,
          dosingHint: result.dosingHint ?? null,
          confidence: result.confidence ?? 'medium',
        });
      }
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  async function handleFile(file: File) {
    try {
      await captureNext(file);
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  if (state.kind === 'candidates') {
    return (
      <CandidatePicker
        candidates={state.candidates}
        dosingHint={state.dosingHint}
        confidence={state.confidence}
        onCancel={() => setState({ kind: 'idle' })}
        onSelected={() => navigate('/medications')}
      />
    );
  }

  // After the first photo lands, prompt to either submit-with-one
  // or capture a second side-panel photo to lift accuracy.
  if (state.kind === 'front-ready') {
    return (
      <AppPage
        title="Got the front. Want to add the side panel?"
        back={{ to: '/medications', label: 'Medications' }}
      >
        <BlockStack gap="400">
          <SeniorCard
            title="A second photo helps a lot"
            body="The side or back of the bottle has the dose, the NDC, and warnings. A second photo lets us read the whole label."
          >
            <img
              src={state.frontPreviewUrl}
              alt="Front of the bottle"
              style={{
                width: '100%',
                borderRadius: 'var(--radius-md)',
                maxHeight: 280,
                objectFit: 'contain',
                marginBottom: 12,
              }}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFile(f);
              }}
              style={{ display: 'none' }}
              data-testid="photo-input-side"
            />
            <SeniorButton onClick={() => fileInputRef.current?.click()} variant="primary" fullWidth>
              Take a side photo
            </SeniorButton>
            <SeniorButton
              onClick={() => void submitPhotos([state.frontBlob], [state.frontPreviewUrl])}
              fullWidth
            >
              Just use the one I took
            </SeniorButton>
          </SeniorCard>
        </BlockStack>
      </AppPage>
    );
  }

  return (
    <AppPage
      title="Take a photo of your medicine bottle"
      back={{ to: '/medications', label: 'Medications' }}
    >
      <BlockStack gap="400">
        <SeniorCard
          title="Hold the bottle steady"
          body="Make sure the label is well lit and you can read the medicine name and strength on your screen before you take the photo."
        >
          {state.kind === 'error' && (
            <SeniorBanner title="Could not read this photo" tone="critical">
              {state.message}
            </SeniorBanner>
          )}
          {state.kind === 'quality-warn' && (
            <SeniorBanner title="This photo might not read well" tone="warning">
              <p style={{ margin: '0 0 12px' }}>{state.message}</p>
              <img
                src={state.previewUrl}
                alt="What we just captured"
                style={{
                  width: '100%',
                  borderRadius: 'var(--radius-md)',
                  maxHeight: 220,
                  objectFit: 'contain',
                  marginBottom: 12,
                }}
              />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <SeniorButton
                  onClick={() => {
                    setState({ kind: 'idle' });
                    fileInputRef.current?.click();
                  }}
                  variant="primary"
                >
                  Retake
                </SeniorButton>
                <SeniorButton
                  onClick={() => {
                    // User overrides our quality warning; capture the
                    // blob into the front-or-submit flow.
                    const blob = state.pendingBlob;
                    const url = state.previewUrl;
                    if (state.kind === 'quality-warn') {
                      // Promote into front-ready so the side-panel
                      // path stays available.
                      setState({
                        kind: 'front-ready',
                        frontBlob: blob,
                        frontPreviewUrl: url,
                      });
                    }
                  }}
                >
                  Use it anyway
                </SeniorButton>
              </div>
            </SeniorBanner>
          )}
          {state.kind === 'no-candidates' && (
            <SeniorBanner
              title={
                state.rawText.trim().length > 0
                  ? 'We read the label but could not identify the medicine'
                  : 'We could not read this label clearly'
              }
              tone="warning"
            >
              {state.rawText.trim().length > 0 ? (
                <>
                  <p style={{ margin: '0 0 12px' }}>
                    Try a closer photo with the brand or generic name fully visible. Or tap below to
                    type the name in.
                  </p>
                  <details style={{ fontSize: 15, color: 'var(--color-text-secondary)' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                      Show what we read
                    </summary>
                    <pre
                      style={{
                        whiteSpace: 'pre-wrap',
                        marginTop: 8,
                        padding: 12,
                        background: 'var(--color-surface-secondary)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: 14,
                        maxHeight: 180,
                        overflow: 'auto',
                      }}
                    >
                      {state.rawText}
                    </pre>
                  </details>
                </>
              ) : (
                'Try a brighter photo with the bottle label flat on the screen, or tap below to type the name in.'
              )}
            </SeniorBanner>
          )}
          {state.kind === 'uploading' && (
            <BlockStack gap="200">
              <div style={{ display: 'flex', gap: 8 }}>
                {state.previewUrls.map((url, i) => (
                  <img
                    key={url}
                    src={url}
                    alt={`Capture ${i + 1}`}
                    style={{
                      flex: 1,
                      borderRadius: 'var(--radius-md)',
                      maxHeight: 220,
                      objectFit: 'contain',
                    }}
                  />
                ))}
              </div>
              <SkeletonBodyText lines={3} />
            </BlockStack>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
            }}
            style={{ display: 'none' }}
            data-testid="photo-input"
          />
          {state.kind !== 'quality-warn' && (
            <SeniorButton
              onClick={() => fileInputRef.current?.click()}
              variant="primary"
              fullWidth
              disabled={state.kind === 'uploading'}
              data-testid="open-camera"
            >
              {state.kind === 'uploading' ? 'Reading the label...' : 'Open camera'}
            </SeniorButton>
          )}
          <SeniorButton
            onClick={() => navigate('/medications/add-by-barcode')}
            fullWidth
            data-testid="barcode-instead"
          >
            Scan the barcode instead
          </SeniorButton>
          <SeniorButton
            onClick={() => navigate('/medications/add-by-name')}
            fullWidth
            data-testid="type-instead"
          >
            Type the name instead
          </SeniorButton>
        </SeniorCard>
      </BlockStack>
    </AppPage>
  );
}

async function resizeImage(file: File | Blob, maxDim: number): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D not supported in this browser');
  ctx.drawImage(bitmap, 0, 0, width, height);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Canvas could not encode the image'))),
      'image/jpeg',
      0.85,
    );
  });
}
